import React, { useMemo, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  setDriverApproved, setDriverBlocked, setDriverNightPermit, updateDriverProfile,
  adminDeleteAccount,
  setDriverDispatchControl,
  formatTime, formatPhone, phoneHref, waHref, getDriverChecklist, listenAllRideEvents,
  getFleetZonesForAdmin,
} from '../services/firebase';
import { downloadCsv } from '../utils/exportCsv';
import { STATUS_LABEL, STATUS_CLASS } from '../utils/rides';
import { hasDocPendingReview, hasMinVerifiedDocs, hasMinVehicleSafetyDocs, driverUpdatedMillis } from '../utils/driverDocs';
import { submissionState, approvalWarning, sortForReview } from '../utils/kycReview';
import DeleteAccountModal from '../components/DeleteAccountModal';
import Anna360Modal from '../components/Anna360Modal';
import DriverDocsModal from '../components/DriverDocsModal';
import ImageViewerModal from '../components/ImageViewerModal';
import { useLang } from '../lang/LangContext';
import { isDuesHeld as isDuesHeldDriver } from '../utils/duesHold';

const emptyEdit = () => ({
  name: '',
  phone: '',
  vehicleNumber: '',
  adminNote: '',
});

const dispatchHoldState = (driver) => String(driver?.adminDispatchHold?.state || '');
const isDispatchHeld = (driver) => dispatchHoldState(driver) === 'after_trip';

export default function Drivers({ drivers, rides = [], initialFilter, isSuperAdmin = false }) {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState(initialFilter || 'all');
  const [townFilter, setTownFilter] = useState('all');
  const [zonesById, setZonesById] = useState({});
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [viewerImage, setViewerImage] = useState(null);
  const [dossierDriverId, setDossierDriverId] = useState(null);
  const [dossierTab, setDossierTab] = useState('complaints');
  const [docModalDriverId, setDocModalDriverId] = useState(null);
  const [editDriverId, setEditDriverId] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit());
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Derived live driver objects (100% reactive to Firestore updates while modal is open)
  const dossierDriver = useMemo(() => (dossierDriverId ? drivers.find((d) => d.id === dossierDriverId) || null : null), [dossierDriverId, drivers]);
  const docModalDriver = useMemo(() => (docModalDriverId ? drivers.find((d) => d.id === docModalDriverId) || null : null), [docModalDriverId, drivers]);
  const editDriver = useMemo(() => (editDriverId ? drivers.find((d) => d.id === editDriverId) || null : null), [editDriverId, drivers]);

  useEffect(() => {
    const unsub = listenAllRideEvents(setEvents);
    return () => unsub?.();
  }, []);

  // Which town each online driver is currently in, resolved live from GPS —
  // never stored, so it's refetched on an interval rather than listened to.
  // Same 30s cadence as the Live Map's own polling fallback (Dispatch.js) —
  // frequent enough for an ops list, without adding meaningful function cost.
  useEffect(() => {
    let cancelled = false;
    const fetchZones = async () => {
      try {
        const list = await getFleetZonesForAdmin();
        if (cancelled) return;
        const byId = {};
        list.forEach((d) => { byId[d.id] = d; });
        setZonesById(byId);
      } catch (err) {
        console.warn('Fleet zones fetch warning:', err?.message);
      }
    };
    fetchZones();
    const interval = setInterval(fetchZones, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const townOptions = useMemo(() => {
    const seen = new Map();
    Object.values(zonesById).forEach((z) => {
      if (z.zoneId && !seen.has(z.zoneId)) {
        seen.set(z.zoneId, z.zoneName === 'default' ? 'Piler (default)' : (z.zoneName || z.zoneId));
      }
    });
    return Array.from(seen.entries());
  }, [zonesById]);

  useEffect(() => {
    if (!editDriver) return;
    setEditForm({
      name: editDriver.name || '',
      phone: String(editDriver.phone || '').replace(/\D/g, '').slice(-10),
      vehicleNumber: editDriver.vehicleNumber || '',
      adminNote: editDriver.adminNote || '',
    });
  }, [editDriverId]);

  // 1. Map rides by driverId
  const ridesByDriver = useMemo(() => {
    const map = {};
    (rides || []).forEach((r) => {
      if (r.driverId) {
        if (!map[r.driverId]) map[r.driverId] = [];
        map[r.driverId].push(r);
      }
    });
    return map;
  }, [rides]);

  // 2. Map events by rideId
  const eventsByRide = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => {
      if (e.rideId) {
        if (!map[e.rideId]) map[e.rideId] = [];
        map[e.rideId].push(e);
      }
    });
    return map;
  }, [events]);

  // 3. Compute real driver dossier metrics
  const driverMetrics = useMemo(() => {
    const map = {};
    (drivers || []).forEach((d) => {
      const dRides = (ridesByDriver[d.id] || []).sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      });
      const dCompleted = dRides.filter((r) => r.status === 'completed');
      const dCancelled = dRides.filter((r) => r.status === 'cancelled');

      // Authoritative non-decreasing lifetime trips and earnings
      const totalCompletedTrips = Math.max(
        Number(d.totalCompletedRides) || 0,
        Number(d.lifetimeRides) || 0,
        Number(d.completedRides) || 0,
        Number(d.totalRides) || 0,
        dCompleted.length
      );
      const driverFareEarnings = Number(d.totalFareEarnings) || 0;
      const rideFareSum = dCompleted.reduce((sum, r) => sum + (Number(r.fare) || 0), 0);
      const totalCash = driverFareEarnings > 0
        ? Math.max(driverFareEarnings, rideFareSum)
        : rideFareSum;

      // Collect all real events for driver's rides
      const dEvents = [];
      dRides.forEach((r) => {
        const rEvts = eventsByRide[r.id] || [];
        dEvents.push(...rEvts);
      });

      // Specific complaints (Real Data)
      const extraMoneyIncidents = dEvents.filter((e) => e.type === 'extra_money_asked' && e.asked);
      const wrongFareIncidents = dEvents.filter((e) => e.type === 'fare_check' && e.correct === false);
      const badRouteIncidents = dEvents.filter((e) => e.type === 'route_check' && e.good === false);
      const plateMismatchIncidents = dRides.filter((r) => r.vehicleMatch === false);
      const sosIncidents = dEvents.filter((e) => e.type === 'sos_action');
      const lowRatingRides = dRides.filter((r) => r.rating && Number(r.rating) <= 2);

      const complaintCount = extraMoneyIncidents.length + wrongFareIncidents.length + badRouteIncidents.length + plateMismatchIncidents.length + sosIncidents.length + lowRatingRides.length;

      // Calculate Automated Driver Risk Score (Module F)
      let riskScore = 100;
      const riskFactors = [];

      if (extraMoneyIncidents.length > 0) {
        riskScore -= extraMoneyIncidents.length * 25;
        riskFactors.push(`Extra money asked (${extraMoneyIncidents.length}x)`);
      }
      if (plateMismatchIncidents.length > 0) {
        riskScore -= plateMismatchIncidents.length * 30;
        riskFactors.push(`Auto plate mismatch reported (${plateMismatchIncidents.length}x)`);
      }
      if (dCancelled.length >= 3) {
        riskScore -= 15;
        riskFactors.push(`Frequent cancellations (${dCancelled.length} trips)`);
      }
      if (lowRatingRides.length > 0) {
        riskScore -= lowRatingRides.length * 10;
        riskFactors.push(`Low rating ≤ 2★ (${lowRatingRides.length}x)`);
      }
      if (sosIncidents.length > 0) {
        riskScore -= 30;
        riskFactors.push(`SOS triggered during trip`);
      }
      riskScore = Math.max(0, Math.min(100, riskScore));
      const riskLevel = riskScore < 60 ? 'high' : riskScore < 85 ? 'moderate' : 'low';

      // Positive praise tags (Real Data)
      const praises = {
        polite: dRides.filter((r) => r.ratingTags?.includes('polite')).length,
        clean: dRides.filter((r) => r.ratingTags?.includes('clean')).length,
        safeDriving: dRides.filter((r) => r.ratingTags?.includes('safeDriving')).length,
        onTime: dRides.filter((r) => r.ratingTags?.includes('onTime')).length,
        goodRoute: dRides.filter((r) => r.ratingTags?.includes('goodRoute')).length,
      };
      const totalPraises = Object.values(praises).reduce((a, b) => a + b, 0);

      map[d.id] = {
        rides: dRides,
        completedRides: dCompleted,
        cancelledRides: dCancelled,
        totalCompletedTrips,
        totalCash,
        events: dEvents,
        extraMoneyIncidents,
        wrongFareIncidents,
        badRouteIncidents,
        plateMismatchIncidents,
        sosIncidents,
        lowRatingRides,
        complaintCount,
        riskScore,
        riskLevel,
        riskFactors,
        praises,
        totalPraises,
      };
    });
    return map;
  }, [drivers, ridesByDriver, eventsByRide]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const list = drivers.filter((d) => {
      const m = driverMetrics[d.id] || {};
      if (filter === 'risk' && m.riskLevel !== 'high') return false;
      if (filter === 'flagged' && !(m.complaintCount > 0)) return false;
      if (filter === 'top' && !((Number(d.ratingAvg) >= 4.8 && (Number(d.ratingCount) >= 3 || m.totalCompletedTrips >= 5)) || m.totalPraises >= 3)) return false;
      if (filter === 'pending' && (d.isApproved || d.isBlocked)) return false;
      if (filter === 'kyc_submitted' && (d.isApproved || d.isBlocked || submissionState(d) !== 'submitted')) return false;
      if (filter === 'docs_review' && !hasDocPendingReview(d)) return false;
      if (filter === 'docs_verified' && !hasMinVerifiedDocs(d, 3)) return false;
      if (filter === 'safety_ready' && !(d.vehicleSafety?.published === true && hasMinVehicleSafetyDocs(d, 3))) return false;
      if (filter === 'photo_review' && !(d.photosPendingReview || d.pendingPhotoUrl || d.pendingVehiclePhotoUrl)) return false;
      if (filter === 'online' && !(d.isOnline && d.isApproved && !d.isBlocked)) return false;
      if (filter === 'offline' && d.isOnline) return false;
      if (filter === 'blocked' && !d.isBlocked) return false;
      if (filter === 'approved' && !d.isApproved) return false;
      if (townFilter !== 'all' && zonesById[d.id]?.zoneId !== townFilter) return false;
      if (!qq) return true;
      const hay = [d.name, d.phone, d.vehicleNumber, d.id].join(' ').toLowerCase();
      return hay.includes(qq);
    });
    // Review queue: most recent uploader first.
    if (filter === 'docs_review') {
      list.sort((a, b) => driverUpdatedMillis(b) - driverUpdatedMillis(a));
    }
    // Approval queue: applications that submitted their documents first, longest-waiting first.
    if (filter === 'pending' || filter === 'kyc_submitted') {
      return sortForReview(list, driverUpdatedMillis);
    }
    return list;
  }, [drivers, driverMetrics, filter, q, townFilter, zonesById]);

  const act = async (uid, fn, okMsg) => {
    setBusy(uid);
    try {
      await fn();
      toast.success(okMsg);
    } catch (e) {
      console.error(e);
      toast.error('Action failed');
    } finally {
      setBusy(null);
    }
  };

  const controlDispatch = async (driver, action) => {
    if (!driver?.id) return;
    const activeTrip = Boolean(driver.activeRideId);
    const isClear = action === 'clear';
    const message = isClear
      ? `Cancel the scheduled offline action for ${driver.name || 'this Anna'}?`
      : activeTrip
        ? `Stop new ride requests for ${driver.name || 'this Anna'} after the current trip ends? The active trip and passenger tracking will continue normally.`
        : `Take ${driver.name || 'this Anna'} offline now? New ride requests and location sharing will stop.`;
    if (!window.confirm(message)) return;
    setBusy(driver.id);
    try {
      const result = await setDriverDispatchControl(driver.id, action);
      const mode = result?.mode;
      toast.success(mode === 'after_trip'
        ? `${driver.name || 'Anna'} will go offline after this trip`
        : mode === 'cleared'
          ? `Scheduled offline action cancelled for ${driver.name || 'Anna'}`
          : `${driver.name || 'Anna'} is offline and removed from dispatch`);
    } catch (error) {
      console.error(error);
      toast.error('Could not update dispatch control');
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    if (!editDriver?.id) return;
    const name = editForm.name.trim();
    const phone = editForm.phone.replace(/\D/g, '').slice(-10);
    const vehicleNumber = editForm.vehicleNumber.trim().toUpperCase();
    if (!name) { toast.error('Name is required'); return; }
    if (phone.length !== 10) { toast.error('Enter a valid 10-digit phone'); return; }
    if (vehicleNumber.length < 4) { toast.error('Enter full auto number (min 4 chars)'); return; }

    setSavingEdit(true);
    try {
      await updateDriverProfile(editDriver.id, {
        name,
        phone,
        vehicleNumber,
        adminNote: editForm.adminNote,
      });
      toast.success('Anna profile updated');
      setEditDriverId(null);
    } catch (e) {
      console.error(e);
      const code = e?.message;
      if (code === 'name_required') toast.error('Name is required');
      else if (code === 'phone_invalid') toast.error('Enter a valid 10-digit phone');
      else if (code === 'vehicle_invalid') toast.error('Enter full auto number');
      else toast.error('Could not save');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async (confirmPhone) => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const res = await adminDeleteAccount(deleteTarget.id, 'driver', confirmPhone);
      const n = Number(res?.cancelledRides) || 0;
      toast.success(n ? `Anna deleted (${n} active ride cancelled)` : 'Anna deleted');
      setDeleteTarget(null);
      if (docModalDriver?.id === deleteTarget.id) setDocModalDriver(null);
      if (editDriver?.id === deleteTarget.id) setEditDriver(null);
      if (dossierDriver?.id === deleteTarget.id) setDossierDriver(null);
    } catch (e) {
      console.error(e);
      const msg = e?.message || '';
      if (msg.includes('Phone does not match')) toast.error('Phone does not match');
      else if (msg.includes('not-found') || msg.includes('Captain not found')) toast.error('Anna not found');
      else toast.error(msg.replace(/^Firebase:\s*/i, '').slice(0, 120) || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const totalFlaggedAnnas = useMemo(() => {
    return (drivers || []).filter((d) => (driverMetrics[d.id]?.complaintCount || 0) > 0).length;
  }, [drivers, driverMetrics]);

  const totalHighRiskAnnas = useMemo(() => {
    return (drivers || []).filter((d) => driverMetrics[d.id]?.riskLevel === 'high').length;
  }, [drivers, driverMetrics]);

  const totalPhotoReviewAnnas = useMemo(() => {
    return (drivers || []).filter(
      (d) => d.photosPendingReview || d.pendingPhotoUrl || d.pendingVehiclePhotoUrl,
    ).length;
  }, [drivers]);

  const totalDocsReviewAnnas = useMemo(
    () => (drivers || []).filter(hasDocPendingReview).length,
    [drivers],
  );

  const totalDocsVerifiedAnnas = useMemo(
    () => (drivers || []).filter((d) => hasMinVerifiedDocs(d, 3)).length,
    [drivers],
  );
  const totalSafetyReadyAnnas = useMemo(
    () => (drivers || []).filter((d) => d.vehicleSafety?.published === true && hasMinVehicleSafetyDocs(d, 3)).length,
    [drivers],
  );

  const totalKycSubmittedAnnas = useMemo(
    () => (drivers || []).filter((d) => !d.isApproved && !d.isBlocked && submissionState(d) === 'submitted').length,
    [drivers],
  );
  const totalPendingApprovalAnnas = useMemo(
    () => (drivers || []).filter((d) => !d.isApproved && !d.isBlocked).length,
    [drivers],
  );

  return (
    <>
      <div className="toolbar">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">{t('All Annas')} ({drivers.length})</option>
          <option value="risk">🔴 {t('High Risk')} ({totalHighRiskAnnas})</option>
          <option value="flagged">🚩 {t('Has Complaints')} ({totalFlaggedAnnas})</option>
          <option value="online">{t('Online now')}</option>
          <option value="offline">{t('Offline')}</option>
          <option value="top">⭐ {t('Top Rated')}</option>
          <option value="pending">{t('Pending approval')} ({totalPendingApprovalAnnas})</option>
          <option value="kyc_submitted">📋 {t('KYC submitted, waiting')} ({totalKycSubmittedAnnas})</option>
          <option value="docs_review">📄 {t('Docs waiting review')} ({totalDocsReviewAnnas})</option>
          <option value="docs_verified">✅ {t('Docs Verified (3+)')} ({totalDocsVerifiedAnnas})</option>
          <option value="safety_ready">🛡️ {t('Vehicle Safety Ready')} ({totalSafetyReadyAnnas})</option>
          <option value="photo_review">📷 {t('Photo waiting review')} ({totalPhotoReviewAnnas})</option>
          <option value="approved">{t('Approved')}</option>
          <option value="blocked">{t('Blocked / Suspended')}</option>
        </select>
        {townOptions.length > 0 && (
          <select value={townFilter} onChange={(e) => setTownFilter(e.target.value)}>
            <option value="all">{t('All towns')}</option>
            {townOptions.map(([zoneId, zoneName]) => (
              <option key={zoneId} value={zoneId}>{zoneName}</option>
            ))}
          </select>
        )}
        <input placeholder={t('Search name, phone, vehicle, ID…')} value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="muted">{filtered.length} {t('Annas')}</span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => downloadCsv('annas-fleet.csv', [
            ['id', 'name', 'phone', 'vehicle', 'town', 'approved', 'online', 'blocked', 'rating', 'ridesRated', 'complaints', 'cashCollected'],
            ...filtered.map((d) => {
              const m = driverMetrics[d.id] || {};
              const zone = zonesById[d.id];
              const town = !d.isOnline ? '' : (zone?.zoneId ? (zone.zoneName === 'default' ? 'Piler' : zone.zoneName) : (zone ? 'out_of_area' : ''));
              return [
                d.id, d.name, d.phone, d.vehicleNumber, town,
                d.isApproved ? 'yes' : 'no', d.isOnline ? 'yes' : 'no', d.isBlocked ? 'yes' : 'no',
                d.ratingAvg || '', d.ratingCount || 0, m.complaintCount || 0, m.totalCash || 0,
              ];
            }),
          ])}
        >
          {t('Export CSV')}
        </button>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty">{t('No Annas match this filter')}</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('ANNA / DRIVER')}</th>
                  <th>{t('VEHICLE')}</th>
                  <th>{t('TOWN')}</th>
                  <th>{t('PERFORMANCE')}</th>
                  <th>{t('STATUS & FLAGS')}</th>
                  <th>{t('CHECKLIST')}</th>
                  <th>{t('ACTIONS')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d, index) => {
                  const check = getDriverChecklist(d);
                  const m = driverMetrics[d.id] || {};
                  const hasIssues = m.complaintCount > 0;

                  const avatarPhoto = d.photoUrl || d.pendingPhotoUrl || d.driverPhoto || d.profilePhoto;
                  return (
                    <tr key={d.id} className={hasIssues ? 'row-warn' : ''}>
                      <td className="muted mono" style={{ fontSize: 12 }}>{index + 1}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          {avatarPhoto ? (
                            <img
                              src={avatarPhoto}
                              alt=""
                              style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)' }}
                              title="Click to view full photo"
                              onClick={() => setViewerImage({
                                url: avatarPhoto,
                                title: `${d.name || 'Anna'} — Profile Photo`,
                                subtitle: `Vehicle: ${d.vehicleNumber || 'Auto'} · Phone: ${formatPhone(d.phone)}`,
                              })}
                            />
                          ) : (
                            <div style={{
                              width: 40, height: 40, borderRadius: 10, background: 'var(--surface)',
                              display: 'grid', placeItems: 'center', fontSize: 18,
                            }}
                            >
                              🛺
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{d.name || 'Anna'}</div>
                            <div className="muted mono" style={{ fontSize: 12 }}>{formatPhone(d.phone)}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="mono" style={{ fontWeight: 700 }}>{d.vehicleNumber || '—'}</div>
                        <div className="muted" style={{ fontSize: 11 }}>{d.vehicleModel || 'Bajaj RE Auto'}</div>
                      </td>
                      <td>
                        {(() => {
                          const zone = zonesById[d.id];
                          if (!d.isOnline) return <span className="muted" style={{ fontSize: 11 }}>{t('Offline')}</span>;
                          if (!zone) return <span className="muted" style={{ fontSize: 11 }}>—</span>;
                          if (!zone.zoneId) {
                            return <span className="badge orange" title={t('Last GPS fix falls outside every configured town')}>{t('Out of area')}</span>;
                          }
                          const label = zone.zoneName === 'default' ? 'Piler' : zone.zoneName;
                          return <span className="badge blue">📍 {label}</span>;
                        })()}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {d.ratingAvg ? `⭐ ${Number(d.ratingAvg).toFixed(1)}` : '⭐ New'}
                          <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>
                            ({d.ratingCount || 0} reviews)
                          </span>
                        </div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                          {m.totalCompletedTrips} trips · ₹{m.totalCash} cash
                        </div>
                      </td>
                      <td>
                        <div className="row-actions" style={{ flexWrap: 'wrap', gap: 4 }}>
                          {d.isBlocked && <span className="badge red">{t('Blocked')}</span>}
                          {!d.isBlocked && !d.isApproved && <span className="badge orange">{t('Pending')}</span>}
                          {!d.isBlocked && !d.isApproved && (submissionState(d) === 'submitted'
                            ? <span className="badge green" title={t('He sent his finished documents for approval')}>📋 {t('KYC submitted')}</span>
                            : <span className="badge gray" title={t('Still filling in documents, or signed up on an older app version')}>{t('No KYC yet')}</span>)}
                          {!d.isBlocked && d.isApproved && <span className="badge green">{t('Approved')}</span>}
                          {(d.photosPendingReview || d.pendingPhotoUrl || d.pendingVehiclePhotoUrl) && (
                            <span className="badge yellow" title={t('New profile / auto photo waiting for review')}>📷 {t('Photo')} ⏳</span>
                          )}
                          {dispatchHoldState(d) === 'after_trip' && <span className="badge orange">{t('After trip hold')}</span>}
                          {isDuesHeldDriver(d) && <span className="badge orange">{t('Dues hold')}</span>}
                          {d.isOnline && !d.isBlocked && d.isApproved && <span className="badge blue">{t('Online')}</span>}
                          {m.riskLevel === 'high' ? (
                            <span className="badge red" style={{ fontWeight: 700 }} title={m.riskFactors?.join(' · ')}>
                              🔴 {t('Risk')} {m.riskScore}
                            </span>
                          ) : m.riskLevel === 'moderate' ? (
                            <span className="badge orange" style={{ fontWeight: 700 }} title={m.riskFactors?.join(' · ')}>
                              🟡 {t('Risk')} {m.riskScore}
                            </span>
                          ) : (
                            <span className="badge green" style={{ fontWeight: 700 }}>
                              🟢 Safe {m.riskScore}
                            </span>
                          )}
                          {hasIssues && (
                            <span className="badge red" title="Has passenger complaints">
                              🚨 {m.complaintCount} Flag{m.complaintCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="row-actions" style={{ flexWrap: 'wrap', gap: 4 }}>
                          <span className={`badge ${check.photoOk ? 'green' : 'red'}`}>Photo</span>
                          <span className={`badge ${check.vehiclePhotoOk ? 'green' : 'red'}`}>Auto</span>
                          <span className={`badge ${check.vehicleOk ? 'green' : 'red'}`}>Plate</span>
                          <span className={`badge ${check.phoneOk ? 'green' : 'red'}`}>Phone</span>
                          {d.documents?.drivingLicence?.status === 'verified' ? (
                            <span className="badge green">DL ✓</span>
                          ) : d.documents?.drivingLicence?.status === 'pending' ? (
                            <span className="badge yellow">DL ⏳</span>
                          ) : null}
                          {d.documents?.rc?.status === 'verified' ? (
                            <span className="badge green">RC ✓</span>
                          ) : d.documents?.rc?.status === 'pending' ? (
                            <span className="badge yellow">RC ⏳</span>
                          ) : null}
                          {d.documents?.insurance?.status === 'verified' ? (
                            <span className="badge green">Ins ✓</span>
                          ) : d.documents?.insurance?.status === 'pending' ? (
                            <span className="badge yellow">Ins ⏳</span>
                          ) : null}
                          {d.documents?.aadhaar?.status === 'verified' ? (
                            <span className="badge green">Aadhaar ✓</span>
                          ) : d.documents?.aadhaar?.status === 'pending' ? (
                            <span className="badge yellow">Aadhaar ⏳</span>
                          ) : null}
                          {['rc', 'insurance', 'drivingLicence'].map((key) => {
                            const status = d.vehicleSafety?.[key]?.status;
                            if (!status) return null;
                            const label = key === 'insurance' ? 'Ins' : key === 'drivingLicence' ? 'DL' : 'RC';
                            return <span key={key} className={`badge ${status === 'valid' ? 'green' : status === 'pending' ? 'yellow' : 'red'}`}>{label} {status === 'valid' ? '✓' : status === 'pending' ? '⏳' : '!'}</span>;
                          })}
                          {d.vehicleSafety?.published === true && <span className="badge blue">Passenger view</span>}
                        </div>
                      </td>
                      <td>
                        <div className="row-actions">
                          {!d.isApproved && !d.isBlocked && (
                            <button
                              type="button"
                              className="btn btn-green btn-sm"
                              disabled={busy === d.id}
                              onClick={() => {
                                // Approving without a complete file is allowed (Annas on older app versions
                                // cannot submit documents), but never by accident.
                                const warning = approvalWarning(d);
                                if (warning && !window.confirm(`${d.name || 'This Anna'}\n\n${warning}\n\nApprove anyway?`)) return;
                                act(d.id, () => setDriverApproved(d.id, true), `${d.name || 'Anna'} approved`);
                              }}
                            >
                              {busy === d.id ? t('Approving…') : t('✓ Approve')}
                            </button>
                          )}
                          {d.isApproved && !d.isBlocked && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.3)' }}
                              disabled={busy === d.id}
                              onClick={() => act(d.id, () => setDriverApproved(d.id, false), `${d.name || 'Anna'} unapproved`)}
                            >
                              {t('Unapprove')}
                            </button>
                          )}
                          {isSuperAdmin && d.isApproved && !d.isBlocked && isDispatchHeld(d) && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#0f766e', borderColor: 'rgba(13, 148, 136, 0.35)' }}
                              disabled={busy === d.id}
                              onClick={() => controlDispatch(d, 'clear')}
                              title="Keep this Anna available after their current trip"
                            >
                              {busy === d.id ? t('Updating…') : t('Cancel scheduled offline')}
                            </button>
                          )}
                          {isSuperAdmin && d.isApproved && !d.isBlocked && !isDispatchHeld(d) && (d.isOnline || d.activeRideId) && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#dc2626', borderColor: 'rgba(220, 38, 38, 0.35)' }}
                              disabled={busy === d.id}
                              onClick={() => controlDispatch(d, 'suspend')}
                            >
                              {busy === d.id ? t('Updating…') : d.activeRideId ? `⏳ ${t('Stop after trip')}` : `🔴 ${t('Take offline')}`}
                            </button>
                          )}
                          {isSuperAdmin && d.isApproved && !d.isBlocked && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              style={d.nightPermit
                                ? { color: '#0f766e', borderColor: 'rgba(13, 148, 136, 0.35)' }
                                : { color: '#64748b', borderColor: 'rgba(100, 116, 139, 0.3)' }}
                              disabled={busy === d.id}
                              onClick={() => act(
                                d.id,
                                () => setDriverNightPermit(d.id, !d.nightPermit),
                                d.nightPermit ? `${d.name || 'Anna'} night permit revoked` : `${d.name || 'Anna'} granted a night permit`,
                              )}
                              title="Allow this Anna to accept rides during the admin's configured night window (Settings → Night operations)"
                            >
                              {busy === d.id ? t('Updating…') : d.nightPermit ? `🌙 ${t('Night: ON')}` : `🌙 ${t('Night: OFF')}`}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            style={{ background: '#0284c7', borderColor: '#0284c7' }}
                            onClick={() => {
                              setDossierDriverId(d.id);
                              setDossierTab(hasIssues ? 'complaints' : 'praises');
                            }}
                          >
                            {t('📋 360 Dossier')}
                          </button>
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setEditDriverId(d.id)}
                            >
                              {t('✏️ Edit')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setDocModalDriverId(d.id)}
                          >
                            {t('📄 Docs')}
                          </button>
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                              onClick={() => setDeleteTarget(d)}
                            >
                              {t('🗑️ Delete')}
                            </button>
                          )}
                          {phoneHref(d.phone) && <a className="btn btn-ghost btn-sm" href={phoneHref(d.phone)}>{t('Call')}</a>}
                          {waHref(d.phone) && <a className="btn btn-ghost btn-sm" href={waHref(d.phone)} target="_blank" rel="noreferrer">WA</a>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ANNA 360 DOSSIER MODAL (100% REAL DATA) */}
      <Anna360Modal
        driver={dossierDriver}
        metrics={driverMetrics[dossierDriverId] || {}}
        onClose={() => setDossierDriverId(null)}
        onEdit={isSuperAdmin ? (d) => {
          setDossierDriverId(null);
          setEditDriverId(d?.id || dossierDriverId);
        } : undefined}
        onDelete={isSuperAdmin ? (d) => {
          setDossierDriverId(null);
          setDeleteTarget(d || dossierDriver);
        } : undefined}
        onDispatchControl={isSuperAdmin ? controlDispatch : undefined}
        act={act}
        isSuperAdmin={isSuperAdmin}
      />

      <DeleteAccountModal
        open={Boolean(deleteTarget)}
        role="driver"
        account={deleteTarget}
        busy={deleting}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={confirmDelete}
      />

      {/* EDIT MODAL */}
      {editDriver && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setEditDriverId(null)}
        >
          <div className="modal-sheet" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className="card-title" style={{ margin: 0 }}>✏️ Edit Anna Profile</div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditDriverId(null)}>✕</button>
            </div>

            <label className="field-label">Name</label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value.slice(0, 60) }))}
              placeholder="Anna name"
              autoFocus
            />

            <label className="field-label" style={{ marginTop: 12 }}>Phone</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="muted">+91</span>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={editForm.phone}
                onChange={(e) => setEditForm((f) => ({
                  ...f,
                  phone: e.target.value.replace(/\D/g, '').slice(0, 10),
                }))}
                placeholder="10-digit mobile"
                style={{ flex: 1 }}
              />
            </div>

            <label className="field-label" style={{ marginTop: 12 }}>Auto number</label>
            <input
              type="text"
              className="mono"
              value={editForm.vehicleNumber}
              onChange={(e) => setEditForm((f) => ({
                ...f,
                vehicleNumber: e.target.value.toUpperCase().slice(0, 32),
              }))}
              placeholder="e.g. AP03AB1234"
            />

            <label className="field-label" style={{ marginTop: 12 }}>Admin note (optional)</label>
            <textarea
              rows={3}
              value={editForm.adminNote}
              onChange={(e) => setEditForm((f) => ({ ...f, adminNote: e.target.value.slice(0, 300) }))}
              placeholder="Internal note"
              style={{ width: '100%', resize: 'vertical' }}
            />

            <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              UID: <span className="mono">{editDriver.id}</span>
            </p>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                onClick={() => {
                  const target = editDriver;
                  setEditDriverId(null);
                  setDeleteTarget(target);
                }}
              >
                🗑️ Delete Anna
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditDriverId(null)}>Cancel</button>
                <button type="button" className="btn btn-green" disabled={savingEdit} onClick={saveEdit}>
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DOCS MODAL */}
      {docModalDriver && (
        <DriverDocsModal
          driver={docModalDriver}
          onClose={() => setDocModalDriverId(null)}
          onRefresh={() => {}}
        />
      )}

      {/* FULL IMAGE VIEWER */}
      {viewerImage && (
        <ImageViewerModal
          imageUrl={viewerImage.url}
          title={viewerImage.title}
          subtitle={viewerImage.subtitle}
          onClose={() => setViewerImage(null)}
        />
      )}
    </>
  );
}
